import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function subtree(id, members) {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    members.forEach((m) => { if (!out.has(m.id) && out.has(m.nearest_upline_leader_id)) { out.add(m.id); grew = true; } });
  }
  return out;
}

export default function EditMemberDialog({ member, members, leaders, teams, onClose, onDone }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!member) return;
    const parts = (member.name || "").split(" ");
    setF({ first_name: member.first_name || parts.slice(0, -1).join(" ") || member.name || "", last_name: member.last_name || (parts.length > 1 ? parts[parts.length - 1] : ""),
      email: member.email || "", email_confirm: "", upline_team: member.upline_team || "", nearest_upline_leader_id: member.nearest_upline_leader_id || "" });
  }, [member]);
  const blocked = useMemo(() => (member ? subtree(member.id, members) : new Set()), [member, members]);
  if (!member || !f) return null;
  const emailChanged = f.email.trim().toLowerCase() !== (member.email || "").toLowerCase();
  const uplineOpts = leaders.filter((l) => !blocked.has(l.id));
  const ok = f.first_name.trim() && f.last_name.trim() && (!emailChanged || f.email_confirm.trim().toLowerCase() === f.email.trim().toLowerCase());
  const save = async () => {
    setBusy(true);
    try {
      const body = { first_name: f.first_name, last_name: f.last_name, upline_team: f.upline_team || undefined, nearest_upline_leader_id: f.nearest_upline_leader_id || undefined };
      if (emailChanged) Object.assign(body, { email: f.email.trim(), email_confirm: f.email_confirm.trim() });
      await api.patch(`/org/users/${member.id}`, body);
      toast.success("Details updated");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md max-h-[90vh] overflow-auto" data-testid="org-edit-dialog">
        <DialogHeader><DialogTitle>Edit {member.name}</DialogTitle><DialogDescription>Changes are recorded in the audit log.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Preferred first name</Label><Input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="org-edit-first" /></div>
            <div><Label>Last name</Label><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="org-edit-last" /></div>
          </div>
          <div><Label>Google sign-in email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="org-edit-email" /></div>
          {emailChanged && (
            <div className="space-y-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="org-edit-email-warning">
              <p className="text-xs text-amber-900">This only changes which Google account can sign in to this profile in the Hub — it does not change their Google account. They'll be signed out and must then sign in with exactly this Google email. Only do this if they asked you to.</p>
              <Label>Type the new email again</Label>
              <Input type="email" value={f.email_confirm} onChange={(e) => setF({ ...f, email_confirm: e.target.value })} data-testid="org-edit-email-confirm" />
            </div>
          )}
          <div><Label>Nearest upline leader</Label>
            <Select value={f.nearest_upline_leader_id} onValueChange={(v) => { const l = leaders.find((x) => x.id === v); setF({ ...f, nearest_upline_leader_id: v, upline_team: l?.upline_team || f.upline_team }); }}>
              <SelectTrigger className="h-11" data-testid="org-edit-upline"><SelectValue placeholder="Choose a leader" /></SelectTrigger>
              <SelectContent>{uplineOpts.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Team</Label>
            <Select value={f.upline_team} onValueChange={(v) => setF({ ...f, upline_team: v })}>
              <SelectTrigger className="h-11" data-testid="org-edit-team"><SelectValue placeholder="Choose a team" /></SelectTrigger>
              <SelectContent>{teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!ok || busy} className="bg-[#0A1F44] text-white" data-testid="org-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
